import type { WorkspaceDashboardConfig } from "@zooi/contracts";

type Config = WorkspaceDashboardConfig;
type Snapshot = { config: Config; revision: number; status: "saved" | "pending" | "saving" | "error"; dirty: boolean; error: unknown; canUndo: boolean };

/** Serial, revision-checked saves. A failed write stops the queue until a person retries. */
export class DashboardAutosave {
  private saved: Config;
  private history: Config[] = [];
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private interacting = false;
  setInteracting = (value: boolean) => { this.interacting = value; };
  private running: Promise<void> | null = null;
  private state: Snapshot;
  private persist: (config: Config, revision: number) => Promise<unknown>;
  constructor(config: Config, revision: number, persist: (config: Config, revision: number) => Promise<unknown>) {
    this.persist = persist;
    this.saved = config;
    this.state = { config, revision, status: "saved", dirty: false, error: null, canUndo: false };
  }
  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private emit(patch: Partial<Snapshot>) {
    this.state = { ...this.state, ...patch, canUndo: this.history.length > 0 };
    this.listeners.forEach(listener => listener());
  }
  update(config: Config, remember = true) {
    if (JSON.stringify(config) === JSON.stringify(this.state.config)) return;
    if (remember) this.history = [...this.history.slice(-29), this.state.config];
    const dirty = JSON.stringify(config) !== JSON.stringify(this.saved);
    this.emit({ config, dirty, status: this.state.error ? "error" : this.running ? "saving" : dirty ? "pending" : "saved" });
    clearTimeout(this.timer);
    if (dirty && !this.state.error) this.timer = setTimeout(() => void this.flush(), 500);
  }
  undo = () => { const previous = this.history.pop(); if (previous) this.update(previous, false); };
  receive(config: Config, revision: number) {
    if (this.interacting || this.state.dirty || this.running || revision <= this.state.revision) return;
    this.saved = config; this.history = [];
    this.emit({ config, revision, error: null, status: "saved" });
  }
  reload(config: Config, revision: number) {
    if (this.running) return;
    clearTimeout(this.timer); this.history = []; this.saved = config;
    this.emit({ config, revision, dirty: false, error: null, status: "saved" });
  }
  discard = () => this.reload(this.saved, this.state.revision);
  retry = () => { this.emit({ error: null }); return this.flush(); };
  flush = (): Promise<void> => {
    clearTimeout(this.timer);
    if (this.running) return this.running;
    if (!this.state.dirty || this.state.error) return Promise.resolve();
    const run = async () => {
      while (this.state.dirty && !this.state.error) {
        const { config, revision } = this.state;
        this.emit({ status: "saving" });
        try {
          await this.persist(config, revision);
          this.saved = config;
          const dirty = JSON.stringify(this.state.config) !== JSON.stringify(config);
          this.emit({ revision: revision + 1, dirty, status: dirty ? "saving" : "saved" });
        } catch (error) { this.emit({ status: "error", dirty: true, error }); }
      }
    };
    this.running = run().finally(() => { this.running = null; });
    return this.running;
  };
  dispose = () => { clearTimeout(this.timer); };
}
